import User from './Resources/User'
import PaymentCard from './Resources/PaymentCard'
import Transaction from './Resources/Transaction'
import Wallet from './Resources/Wallet'
import Hash from './Modules/Hash'
import './Modules/Bluesnap.v4.js'
import * as Sentry from "@sentry/browser"

const classMapping = {
  'User': User,
  'PaymentCard': PaymentCard,
  'Transaction': Transaction,
  'Wallet': Wallet,
};

class Cashback {

  constructor(token = null, baseUrl = 'https://salience.cashbackapp.com') {
    // Defaults
    this.token = null
    this.publisherClientId = null
    this.dsn = null

    if (token) {
      this.token = 'Bearer ' + token
    }

    this.baseUrl = baseUrl
  }

  hash(str) {
    return Hash(str).toString()
  }

  withBasicAuth(username, password, publisher = true) {
    if (publisher) {
      username = username + '#publisher'
    }

    this.token = 'Basic ' + btoa(unescape(encodeURIComponent(username + ':' + password)))
  }

  withHeaders(headers = {}) {
    this.haeders = headers
  }

  withSentry(dsn = '') {
    if (dsn && dsn != '') {
      this.dsn = dsn
      Sentry.init({
        dsn: dsn,
      });
    }
  }

  async login(username, password, clientId) {
    try {
      const response = await fetch(this.baseUrl + '/oauth/access_token', {
        method: 'POST',
        body: 'grant_type=password&client_id=' + encodeURIComponent(clientId) + '&username=' + encodeURIComponent(username) + '&password=' + encodeURIComponent(password),
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        }
      })

      if (response.status == 503) {
        throw new Error('Du kan ikke logge ind i øjeblikket, da vores systemer er ved at blive opdaterede. Prøv venligst igen senere.')
      } else if (!response.ok) {
        throw new Error('Log ind fejlede.')
      }

      return await response.json()
    } catch (error) {
      this.captureThrow(error)
    }
  }

  async register(user) {
    if (!user instanceof User) {
      this.captureThrow(new Error('Could not register user, please provide instance of user object'))
    }

    if (!this.publisherClientId) {
      this.captureThrow(new Error('Please set the required Publisher Client ID'))
    }

    try {
      const response = await this.request('POST', '/user/register/', user.toObject())
      return new User(response)
    } catch (error) {
      this.captureSentryError(error)
      throw new Error('Der skete en fejl. Vær venlig at prøve igen eller kontakt kundeservice.')
    }
  }

  async users(limit = 10) {
    try {
      const response = await this.request('GET', '/user/list/' + limit)
      let users = []

      response.results.forEach(item => {
        users.push(new User(item))
      })

      return {
        total: response.total,
        items: users
      }
    } catch (error) {
      this.captureThrow(error)
    }
  }

  async user(userId) {
    let path = '/user/get/';

    if (userId) {
      path = path + userId
    }

    try {
      const response = await this.request('GET', path)

      return new User(response)
    } catch (error) {
      this.captureThrow(error)
    }
  }

  async userUpdate(user) {
    let data = {}

    if (!user instanceof User) {
      this.captureThrow(new Error('Could not update user'))
    }

    data = user.toTrimmedObject()

    let userId = data._id
    let path = '/user/update/';

    if (userId) {
      path = path + userId
    }

    // We can never update the id
    if (data._id) {
      delete data._id
    }

    try {
      const response = await this.request('PUT', path, data)
      return new User(response)
    } catch (error) {
      this.captureThrow(error)
    }
  }

  async userDelete(user) {
    if (!user instanceof User) {
      this.captureThrow(new Error('Could not delete user'))
    }

    return await this.request('DELETE', '/user/delete/' + user.id)
  }

  async paymentCards(limit = 10) {
    try {
      const response = await this.request('GET', '/payment_card/list/' + limit)
      let paymentCards = []

      response.results.forEach(item => {
        paymentCards.push(new PaymentCard(item))
      })

      return {
        total: response.total,
        items: paymentCards
      }
    } catch (error) {
      this.captureThrow(error)
    }
  }

  paymentCardRegister(paymentCard) {
    if (!paymentCard instanceof PaymentCard) {
      this.captureThrow(new Error('Could not register payment card'))
    }

    return this.request('POST', '/payment_card/register/DNK/', paymentCard.toObject())
      .then(response => {
        return new PaymentCard(response)
      })
      .catch(response => {
        if ((response == 'Error: Card verification failed') || (response == 'Error: Card verification failed - BS')) {
          this.captureThrow(new Error('Oplysningerne på kortet er ikke korrekte – kontroller venligst dine oplysninger.​'))
        } else if (response == 'Error: Card has expired') {
          this.captureThrow(new Error('Betalingskortet er udløbet'))
        } else if (response == 'Error: This card is already registered at this publisher') {
          this.captureThrow(new Error('Kortet er allerede registreret i dette program.​'))
        } else {
          return this.threeDsecure(paymentCard.toObject())
        }
      })
  }

  async threeDsecure(data) {
    try {
      let token = await this.request('GET', '/bluesnap/payment_token').then(response => {
        if (!response.token) {
          throw new Error('Failed to get 3Dsecure payment token')
        }

        return response.token
      });

      // https://stackoverflow.com/questions/26150232/resolve-javascript-promise-outside-function-scope?rq=1
      const createPromise = () => {
        let resolver;
        return [
          new Promise((resolve, reject) => {
            resolver = resolve;
          }),
          resolver,
        ];
      };

      const [threeDsPaymentsResponse, resolver] = createPromise();

      await bluesnap.threeDsPaymentsSetup(token, (response) => {
        resolver(response)
      })

      await bluesnap.threeDsPaymentsSubmitData({
        'ccNumber': data.pan,
        'cvv': data.cvv,
        'expDate': data.expiry,
        'amount': 0,
        'currency': 'DKK'
      });

      let threeDSecureReferenceId = await threeDsPaymentsResponse.then(response => {
        if (response.code != 1) {
          throw new Error('3Dsecure callback failed')
        }

        return response.threeDSecure.threeDSecureReferenceId
      });

      data.secure_reference_id = threeDSecureReferenceId

      let paymentCard = await this.request('POST', '/payment_card/register/DNK/', data);

      return new PaymentCard(paymentCard)
    } catch (error) {
      this.captureThrow(error)
    }
  }

  async paymentCardDelete(parmentCardId) {
    return await this.request('DELETE', '/payment_card/delete/' + parmentCardId)
  }

  async transactions(limit = 10, offset = 0, sort = 'desc') {
    try {
      const response = await this.request('GET', '/transaction/list/' + limit + '/' + offset + '/?timestamp=' + sort)
      let transactions = []

      response.results.forEach(item => {
        transactions.push(new Transaction(item))
      })

      return {
        total: response.total,
        items: transactions
      }
    } catch (error) {
      this.captureThrow(error)
    }
  }

  async transactionCreate(userId, amount, description = '', currency = 'DKK') {
    if (!amount || amount == 0) {
      throw new Error('Please specify amount')
    }

    try {
      const response = await this.request('POST', '/transaction/create/' + userId,
        {
          amount: amount,
          currency: currency,
          description: description,
        }
      )

      return new PaymentCard(response)
    } catch (error) {
      this.captureThrow(error)
    }
  }

  async balance(currency = 'DKK') {
    return await this.request('GET', '/transaction/balance/' + currency)
  }

  async wallets(limit = 10, offset = 0) {
    try {
      const response = await this.request('GET', '/wallet/list/' + limit + '/' + offset)
      let wallets = []

      response.results.forEach(item => {
        wallets.push(new Wallet(item))
      })

      return {
        total: response.total,
        items: wallets
      }
    } catch (error) {
      this.captureThrow(error)
    }
  }

  async wallet(currency = 'DKK', limit = 10) {
    try {
      const response = await this.request('GET', '/wallet/list/' + limit)
      let wallet = undefined

      if (response.total > 0) {
        // This will take the last wallet of that currency, is that what we want?
        response.results.forEach(item => {
          if (item.currency == currency) {
            wallet = new Wallet(item)
          }
        })
      }

      if (!wallet) {
        throw new Error('Wallet not found')
      }

      return wallet
    } catch (error) {
      this.captureThrow(error)
    }
  }

  async walletWithdraw(walletAddress, sortCode, accountNumber) {
    if (!walletAddress) {
      this.captureThrow(new Error('Please provide wallet address'))
    }

    if (!sortCode || !accountNumber) {
      this.captureThrow(new Error('Please provide bank account details'))
    }

    return await this.request('POST', '/wallet/withdraw/' + walletAddress, {
      'sort_code': sortCode + '',
      'account_number': accountNumber + '',
      'auto_merge_wallets': true,
      'amount': 0,
    })
  }

  async search(entity, query) {
    let endpoint = '/' + entity + '/search'

    try {
      const response = await this.request('POST', endpoint, query)
      let items = []
      let className = entity.charAt(0).toUpperCase() + entity.slice(1)

      response.results.forEach(item => {
        items.push(new classMapping[className](item))
      })

      return {
        total: response.total,
        items: items
      }
    } catch (error) {
      this.captureThrow(error)
    }
  }

  captureSentryError(error) {
    if (this.dsn) {
      Sentry.captureException(error);
    }
  }

  captureThrow(error) {
    this.captureSentryError(error)
    throw error
  }

  // More ways of error handling
  // https://gist.github.com/odewahn/5a5eeb23279eed6a80d7798fdb47fe91
  async request(method, path, data) {
    try {
      const response = await fetch(this.baseUrl + path,
        {
          method: method,
          headers: {
            ...{
              'Authorization': this.token,
              'Content-Type': 'application/json'
            },
            ...this.haeders
          },
          body: JSON.stringify(data),
        })

      if (response.status == 503) {
        throw new Error('Du kan ikke logge ind i øjeblikket, da vores systemer er ved at blive opdaterede. Prøv venligst igen senere.')
      }

      const body = await response.json()

      if (response.ok) {
        return body.data
      }

      let errorbody = body.data[0] ? body.data[0] : body.data

      let errorMessage = errorbody.message ?? ''

      this.captureSentryError(new Error(errorMessage))

      throw new Error(errorMessage)
    } catch (error) {
      this.captureThrow(error)
    }
  }
}

export default Cashback
